import { useAppDispatch } from "@/services/hooks";
import React, { useMemo, useState, useCallback } from "react";

import { editRole } from "@/services/userServices/userActions";
import EditRoleView from "./EditRoleView";
import NotificationAlertOptions from "@/layouts/Alerts";
import type { RoleOption } from "./EditRoleView";
import type {
  EditableUser,
  UsersListContainerProps,
} from "./DisplayUsers/UsersListContainer";

export type EditRoleContainerProps = {
  formModal: boolean;
  toggleModal: () => void;
  /** user object from parent (UsersListContainer) */
  userToUpdate?: EditableUser;
  /** callback to update parent list after successful save */
  userupdated?: UsersListContainerProps["updateRoleHandler"];
};

const EditRoleContainer = ({
  formModal,
  toggleModal,
  userToUpdate,
  userupdated,
}: EditRoleContainerProps) => {
  const dispatch = useAppDispatch();
  // A draft of the selected user. UsersListView keys this component on the
  // user's _id, so picking another user remounts it with a fresh draft
  // (SCRUM-2178: no effect copying the prop into state).
  const [user, setUser] = useState<EditableUser>(userToUpdate || {});

  const currentRoleLabel = useMemo(() => userToUpdate?.role || "", [userToUpdate]);

  const onSelectRole = useCallback((option: RoleOption | null) => {
    setUser(prev => ({ ...prev, role: option ? option.value : "" }));
  }, []);

  const onSave = useCallback(async () => {
    // only save if role actually changed
    const roleChanged = user?.role !== userToUpdate?.role;
    if (!roleChanged) {
      toggleModal();
      return;
    }

    // Only reflect the change in the parent list and close the modal when the role PUT
    // actually succeeded. editRole now re-throws on failure (SCRUM-813), so a server-side
    // error no longer falls through to an optimistic list update + close as if it saved.
    try {
      // SCRUM-1033: editRole is a createAppAsyncThunk now, so .unwrap() is what
      // turns a failed PUT back into a rejection — without it `await dispatch(...)`
      // resolves even on failure and the SCRUM-813 silent success returns.
      await dispatch(editRole(user)).unwrap();
      // reflect the change in the parent list
      if (typeof userupdated === "function") {
        await userupdated(user);
      }
      toggleModal();
    } catch (err) {
      // Errors are surfaced by the shell hub (GlobalNotifications) from state.errors;
      // editRole is a createAppAsyncThunk, so appErrorMiddleware already feeds its
      // displayable payload there. A local toast here rendered a second, identical one
      // on the users view's default ToastContainer (SCRUM-1039). The catch stays so a
      // rejected save does not fall through to the success path above.
    }
  }, [dispatch, user, userToUpdate, toggleModal, userupdated]);

  return (
    <EditRoleView
      formModal={formModal}
      toggleModal={toggleModal}
      user={user}
      currentRoleLabel={currentRoleLabel}
      onSelectRole={onSelectRole}
      onSave={onSave}
    />
  );
};

export default EditRoleContainer;
